import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  FieldValue
} from 'firebase/firestore';
import { db } from './config';

export type FactValue = string | number | boolean | Date | string[] | null;

/**
 * Normalize fact key by removing prefixes (e.g., "flood.isSFHA" -> "isSFHA")
 * This ensures facts use the same keys as checklist field definitions
 */
export function normalizeFactKey(key: string): string {
  // Remove common prefixes: flood., loan., collateral., dates., hmda., cra.
  const prefixes = ['flood.', 'loan.', 'collateral.', 'dates.', 'hmda.', 'cra.'];
  for (const prefix of prefixes) {
    if (key.startsWith(prefix)) {
      return key.substring(prefix.length);
    }
  }
  return key; // Return as-is if no prefix found
}

/**
 * Normalize facts object by removing prefixes from keys
 */
export function normalizeFacts(facts: Record<string, any>): Record<string, any> {
  const normalized: Record<string, any> = {};
  for (const [key, value] of Object.entries(facts)) {
    const normalizedKey = normalizeFactKey(key);
    normalized[normalizedKey] = value;
  }
  return normalized;
}

/**
 * Extracted value from a specific file
 */
export interface ExtractedValue {
  value: FactValue; // string[] for list-type fields (e.g. guarantor names)
  fileId?: string; // File ID if available
  fileName: string; // File name for reference
  confidence?: 'high' | 'medium' | 'low';
  extractedAt?: any; // Timestamp when extracted
}

export interface AssetFact {
  key: string; // e.g., "isImprovedRealEstate", "zone", "isSFHA" (no prefixes)
  value: FactValue; // Primary value
  source: 'CORE_SYSTEM' | 'EXTRACTED' | 'REVIEWER_OVERRIDE';
  
  // Support multiple extracted values (one per file)
  // This allows storing multiple values for the same fact from different files
  extractedValues?: ExtractedValue[];
  
  // Primary file name (for display)
  extractedFrom?: string;
  extractedAt?: any; // Primary extraction timestamp
  overriddenBy?: string; // User ID if source is REVIEWER_OVERRIDE
  overriddenAt?: any; // Timestamp when overridden
  overrideReason?: string; // Reason for override
  confidence?: 'high' | 'medium' | 'low'; // Primary confidence
  lastUpdatedAt?: any;
}

export interface AssetFacts {
  id?: string;
  assetId: string;
  organizationId: string;
  facts: Record<string, AssetFact>; // key -> AssetFact
  createdAt?: any;
  updatedAt?: any;
}

// Helper function to remove undefined values
// Preserves Date objects and other special types
const removeUndefined = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  // Preserve Date objects - Firestore will convert them to Timestamps
  if (obj instanceof Date) {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(removeUndefined);
  }
  
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const key in obj) {
      if (obj[key] !== undefined) {
        cleaned[key] = removeUndefined(obj[key]);
      }
    }
    return cleaned;
  }
  
  return obj;
};

// Helper to convert Firestore Timestamps to Date objects
// Only converts actual Timestamps, not arbitrary strings or objects
const convertTimestampToDate = (value: any): any => {
  if (value === null || value === undefined) return value;
  
  // If it's already a Date object, return it (but validate it)
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  
  // Check if it's a Firestore Timestamp (has toDate method)
  if (value && typeof value.toDate === 'function') {
    try {
      const date = value.toDate();
      return isNaN(date.getTime()) ? null : date;
    } catch (e) {
      return null;
    }
  }
  
  // Check if it's a Timestamp-like object with seconds
  if (typeof value === 'object' && value !== null) {
    // Check for seconds property (Firestore Timestamp format)
    if (value.seconds !== undefined || value._seconds !== undefined) {
      try {
        const seconds = value.seconds || value._seconds || 0;
        const date = new Date(seconds * 1000);
        if (isNaN(date.getTime())) {
          return null;
        }
        return date;
      } catch (e) {
        return null;
      }
    }
    
    // If it's an empty object, return null (this shouldn't happen, but handle it)
    if (Object.keys(value).length === 0) {
      return null;
    }
    
    // If it's an object but not a Timestamp, return as-is (don't convert)
    return value;
  }
  
  // For strings, ONLY convert if it looks like a date string (ISO format: YYYY-MM-DD)
  // Don't convert arbitrary strings like '4451' or '9800'
  if (typeof value === 'string' && value.trim() !== '') {
    // Only try to parse if it matches date-like patterns
    const datePattern = /^\d{4}-\d{2}-\d{2}/; // YYYY-MM-DD format
    if (datePattern.test(value.trim())) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
  }
  
  return value;
};

export const assetFactsService = {
  // Get facts for an asset
  getAssetFacts: async (assetId: string): Promise<{ success: boolean; data?: AssetFacts; error?: string }> => {
    try {
      const factsRef = doc(db, 'asset_facts', assetId);
      const docSnap = await getDoc(factsRef);
      
      if (docSnap.exists()) {
        const data = { id: docSnap.id, ...docSnap.data() } as AssetFacts;
        
        // Convert Timestamps to Date objects in facts
        if (data.facts) {
          const convertedFacts: Record<string, AssetFact> = {};
          for (const [key, fact] of Object.entries(data.facts)) {
            const convertedFact: AssetFact = {
              ...fact,
              value: convertTimestampToDate(fact.value),
            };
            
            // Convert Timestamps in extractedValues array
            if (fact.extractedValues && Array.isArray(fact.extractedValues)) {
              convertedFact.extractedValues = fact.extractedValues.map(ev => ({
                ...ev,
                value: convertTimestampToDate(ev.value),
              }));
            }
            
            convertedFacts[key] = convertedFact;
          }
          data.facts = convertedFacts;
        }
        
        return { success: true, data };
      } else {
        // Return empty facts object
        return { 
          success: true, 
          data: {
            assetId,
            facts: {},
          } as AssetFacts 
        };
      }
    } catch (error: any) {
      console.error('Error getting asset facts:', error);
      return { success: false, error: error.message };
    }
  },

  // Update or set a fact
  setFact: async (
    assetId: string,
    factKey: string,
    value: FactValue,
    source: 'CORE_SYSTEM' | 'EXTRACTED' | 'REVIEWER_OVERRIDE',
    options?: {
      organizationId?: string;
      extractedFrom?: string;
      overriddenBy?: string;
      overrideReason?: string;
      confidence?: 'high' | 'medium' | 'low';
    }
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      if (!assetId) {
        return { success: false, error: 'Asset ID is required' };
      }

      const factsRef = doc(db, 'asset_facts', assetId);
      const docSnap = await getDoc(factsRef);
      
      let existingFacts: Record<string, AssetFact> = {};
      let organizationId = options?.organizationId;

      if (docSnap.exists()) {
        const existing = docSnap.data() as AssetFacts;
        existingFacts = existing.facts || {};
        organizationId = organizationId || existing.organizationId;
      }

      if (!organizationId) {
        return { success: false, error: 'Organization ID is required' };
      }

      // Normalize fact key (remove prefixes like "flood.", "loan.", etc.)
      const normalizedKey = normalizeFactKey(factKey);

      // Check priority: don't overwrite CORE_SYSTEM with EXTRACTED, don't overwrite anything with lower priority
      const existingFact = existingFacts[normalizedKey];
      const priority: Record<string, number> = {
        'CORE_SYSTEM': 3,
        'REVIEWER_OVERRIDE': 2,
        'EXTRACTED': 1,
      };

      if (existingFact && priority[existingFact.source] > priority[source]) {
        // Existing fact has higher priority, don't overwrite
        return { success: true }; // Silently succeed, fact not updated
      }

      // Create or update the fact
      let fact: AssetFact;
      
      if (source === 'EXTRACTED') {
        // For EXTRACTED facts, append to extractedValues array instead of overwriting
        if (existingFact && existingFact.source === 'EXTRACTED') {
          // Append new extracted value to existing array
          const extractedValues = existingFact.extractedValues || [];
          
          // Check if this file already has a value for this fact
          const existingIndex = extractedValues.findIndex(
            ev => ev.fileName === options?.extractedFrom || ev.fileId === options?.extractedFrom
          );
          
          const newExtractedValue: ExtractedValue = {
            value,
            fileName: options?.extractedFrom || 'unknown',
            // Don't set fileId here - we'll rely on fileName matching
            confidence: options?.confidence || 'medium',
            extractedAt: serverTimestamp(),
          };
          
          if (existingIndex >= 0) {
            // Update existing entry for this file
            extractedValues[existingIndex] = newExtractedValue;
          } else {
            // Add new entry
            extractedValues.push(newExtractedValue);
          }
          
          // Use highest confidence value as primary, or most recent
          const highestConfidence = extractedValues.reduce((prev, curr) => {
            const prevConf = prev.confidence === 'high' ? 3 : prev.confidence === 'medium' ? 2 : 1;
            const currConf = curr.confidence === 'high' ? 3 : curr.confidence === 'medium' ? 2 : 1;
            return currConf > prevConf ? curr : prev;
          });
          
          fact = {
            ...existingFact,
            key: normalizedKey,
            value: highestConfidence.value,
            extractedValues,
            extractedFrom: highestConfidence.fileName,
            extractedAt: highestConfidence.extractedAt,
            confidence: highestConfidence.confidence,
            lastUpdatedAt: serverTimestamp(),
          };
        } else {
          // New extracted fact or overwriting non-extracted fact
          const extractedValue: ExtractedValue = {
            value,
            fileName: options?.extractedFrom || 'unknown',
            fileId: options?.extractedFrom?.includes('/') ? undefined : options?.extractedFrom,
            confidence: options?.confidence || 'medium',
            extractedAt: serverTimestamp(),
          };
          
          fact = {
            key: normalizedKey,
            value,
            source,
            extractedValues: [extractedValue],
            extractedFrom: options?.extractedFrom,
            extractedAt: serverTimestamp(),
            confidence: options?.confidence || 'medium',
            lastUpdatedAt: serverTimestamp(),
          };
        }
      } else if (source === 'REVIEWER_OVERRIDE') {
        // Reviewer override replaces everything, but preserve extractedFrom if provided
        fact = {
          key: normalizedKey,
          value,
          source,
          extractedFrom: options?.extractedFrom, // Include file reference if provided
          overriddenBy: options?.overriddenBy,
          overriddenAt: serverTimestamp(),
          overrideReason: options?.overrideReason,
          confidence: options?.confidence || 'high', // Reviewer overrides are high confidence
          lastUpdatedAt: serverTimestamp(),
        };
      } else {
        // CORE_SYSTEM - replace everything
        fact = {
          key: normalizedKey,
          value,
          source,
          lastUpdatedAt: serverTimestamp(),
        };
      }

      existingFacts[normalizedKey] = fact;

      const factsData: Omit<AssetFacts, 'id'> = {
        assetId,
        organizationId,
        facts: existingFacts,
        updatedAt: serverTimestamp(),
      };

      if (!docSnap.exists()) {
        factsData.createdAt = serverTimestamp();
      }

      await setDoc(factsRef, removeUndefined(factsData), { merge: true });

      return { success: true };
    } catch (error: any) {
      console.error('Error setting asset fact:', error);
      return { success: false, error: error.message };
    }
  },

  // Set multiple facts at once
  setFacts: async (
    assetId: string,
    facts: Record<string, { value: FactValue; source: 'CORE_SYSTEM' | 'EXTRACTED' | 'REVIEWER_OVERRIDE'; extractedFrom?: string; confidence?: 'high' | 'medium' | 'low' }>,
    organizationId: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      if (!assetId || !organizationId) {
        return { success: false, error: 'Asset ID and Organization ID are required' };
      }

      const factsRef = doc(db, 'asset_facts', assetId);
      const docSnap = await getDoc(factsRef);
      
      let existingFacts: Record<string, AssetFact> = {};
      if (docSnap.exists()) {
        const existing = docSnap.data() as AssetFacts;
        existingFacts = existing.facts || {};
      }

      const priority: Record<string, number> = {
        'CORE_SYSTEM': 3,
        'REVIEWER_OVERRIDE': 2,
        'EXTRACTED': 1,
      };

      // Update facts, respecting priority and appending extracted values
      Object.entries(facts).forEach(([key, factData]) => {
        // Normalize key (remove prefixes like "flood.", "loan.", etc.)
        const normalizedKey = normalizeFactKey(key);
        const existingFact = existingFacts[normalizedKey];
        
        // Check priority: don't overwrite higher priority sources
        if (existingFact && priority[factData.source] < priority[existingFact.source]) {
          return; // Skip - existing fact has higher priority
        }

        const isWorkflowDerived = factData.extractedFrom === 'Workflow rule (AI)' || factData.extractedFrom === 'Workflow rule' || factData.extractedFrom === 'Workflow rule (cleared)';
        
        if (factData.source === 'EXTRACTED') {
          // Workflow-derived (or cleared): set primary value so old values don't persist; allow null for clear-before-re-evaluate
          if (isWorkflowDerived) {
            const extractedValues = existingFact?.source === 'EXTRACTED' && existingFact.extractedValues
              ? [...existingFact.extractedValues]
              : [];
            const workflowEntry: ExtractedValue = {
              value: factData.value,
              fileName: factData.extractedFrom || 'Workflow rule',
              confidence: factData.confidence || 'high',
              extractedAt: serverTimestamp(),
            };
            const idx = extractedValues.findIndex(ev => ev.fileName === factData.extractedFrom || ev.fileName?.startsWith('Workflow rule'));
            if (idx >= 0) extractedValues[idx] = workflowEntry;
            else if (factData.value !== null && factData.value !== undefined) extractedValues.push(workflowEntry);
            existingFacts[normalizedKey] = {
              key: normalizedKey,
              value: factData.value,
              source: factData.source,
              extractedValues: extractedValues.length > 0 ? extractedValues : undefined,
              extractedFrom: factData.extractedFrom,
              extractedAt: serverTimestamp(),
              confidence: factData.confidence || 'high',
              lastUpdatedAt: serverTimestamp(),
            };
            return;
          }
          
          // For other EXTRACTED facts, append to extractedValues array
          if (existingFact && existingFact.source === 'EXTRACTED' && existingFact.extractedValues) {
            // Append new extracted value
            const extractedValues = [...existingFact.extractedValues];
            
            // Check if this file already has a value
            const existingIndex = extractedValues.findIndex(
              ev => ev.fileName === factData.extractedFrom || ev.fileId === factData.extractedFrom
            );
            
            const newExtractedValue: ExtractedValue = {
              value: factData.value,
              fileName: factData.extractedFrom || 'unknown',
              fileId: factData.extractedFrom?.includes('/') ? undefined : factData.extractedFrom,
              confidence: factData.confidence || 'medium',
              extractedAt: serverTimestamp(),
            };
            
            if (existingIndex >= 0) {
              extractedValues[existingIndex] = newExtractedValue;
            } else {
              extractedValues.push(newExtractedValue);
            }
            
            // Use highest confidence value as primary
            const highestConfidence = extractedValues.reduce((prev, curr) => {
              const prevConf = prev.confidence === 'high' ? 3 : prev.confidence === 'medium' ? 2 : 1;
              const currConf = curr.confidence === 'high' ? 3 : curr.confidence === 'medium' ? 2 : 1;
              return currConf > prevConf ? curr : prev;
            });
            
            existingFacts[normalizedKey] = {
              ...existingFact,
              key: normalizedKey,
              value: highestConfidence.value,
              extractedValues,
              extractedFrom: highestConfidence.fileName,
              extractedAt: highestConfidence.extractedAt,
              confidence: highestConfidence.confidence,
              lastUpdatedAt: serverTimestamp(),
            };
          } else {
            // New extracted fact
            const extractedValue: ExtractedValue = {
              value: factData.value,
              fileName: factData.extractedFrom || 'unknown',
              fileId: factData.extractedFrom?.includes('/') ? undefined : factData.extractedFrom,
              confidence: factData.confidence || 'medium',
              extractedAt: serverTimestamp(),
            };
            
            existingFacts[normalizedKey] = {
              key: normalizedKey,
              value: factData.value,
              source: factData.source,
              extractedValues: [extractedValue],
              extractedFrom: factData.extractedFrom,
              extractedAt: serverTimestamp(),
              confidence: factData.confidence || 'medium',
              lastUpdatedAt: serverTimestamp(),
            };
          }
        } else {
          // Non-extracted source (CORE_SYSTEM or REVIEWER_OVERRIDE) - replace
          existingFacts[normalizedKey] = {
            key: normalizedKey,
            value: factData.value,
            source: factData.source,
            lastUpdatedAt: serverTimestamp(),
          };
        }
      });

      const factsData: Omit<AssetFacts, 'id'> = {
        assetId,
        organizationId,
        facts: existingFacts,
        updatedAt: serverTimestamp(),
      };

      if (!docSnap.exists()) {
        factsData.createdAt = serverTimestamp();
      }

      await setDoc(factsRef, removeUndefined(factsData), { merge: true });

      return { success: true };
    } catch (error: any) {
      console.error('Error setting asset facts:', error);
      return { success: false, error: error.message };
    }
  },

  // Override a fact (reviewer override)
  overrideFact: async (
    assetId: string,
    factKey: string,
    value: FactValue,
    userId: string,
    reason?: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const factsRef = doc(db, 'asset_facts', assetId);
      const docSnap = await getDoc(factsRef);
      
      if (!docSnap.exists()) {
        return { success: false, error: 'Asset facts not found. Please set facts first.' };
      }

      const existing = docSnap.data() as AssetFacts;
      const existingFacts = existing.facts || {};

      // Normalize fact key
      const normalizedKey = normalizeFactKey(factKey);

      const fact: AssetFact = {
        key: normalizedKey,
        value,
        source: 'REVIEWER_OVERRIDE',
        overriddenBy: userId,
        overriddenAt: serverTimestamp(),
        overrideReason: reason,
        lastUpdatedAt: serverTimestamp(),
      };

      existingFacts[normalizedKey] = fact;

      await updateDoc(factsRef, {
        facts: existingFacts,
        updatedAt: serverTimestamp(),
      });

      return { success: true };
    } catch (error: any) {
      console.error('Error overriding asset fact:', error);
      return { success: false, error: error.message };
    }
  },

  // Get a specific fact value
  getFact: async (assetId: string, factKey: string): Promise<{ success: boolean; value?: FactValue; error?: string }> => {
    try {
      const normalizedKey = normalizeFactKey(factKey);
      const result = await assetFactsService.getAssetFacts(assetId);
      if (result.success && result.data) {
        const fact = result.data.facts[normalizedKey];
        return { success: true, value: fact?.value };
      }
      return { success: true, value: null };
    } catch (error: any) {
      console.error('Error getting asset fact:', error);
      return { success: false, error: error.message };
    }
  },
};

